/** Edit an existing work item.
 *
 * The board's cards move status by drag; everything else about an item — its title, its
 * project, which sprint it sits in — was uneditable before this dialog existed, and the
 * only fix for a typo was delete-and-recreate, which loses the item's history.
 */

import { useState } from 'react'

import { SearchSelect } from '@/components/SearchSelect'
import { Button, ErrorState, Field, inputClass } from '@/components/ui'
import { useSession } from '@/lib/session'
import { MilestoneField } from '@/features/delivery/MilestoneField'
import { AssigneePicker } from '@/features/people/AssigneePicker'
import { type AssigneeValue, assigneeFields } from '@/features/people/assignee'
import { projectOptions } from '@/features/projects/projectOptions'
import { useProjects } from '@/features/projects/useProjects'
import { TrackerSourceFields, type TrackerSource } from '@/features/workbench/TrackerSourceFields'
import {
  useAddWorkItemComment,
  useSprints,
  useUpdateWorkItem,
  useWorkItemComments,
  type WorkItem,
} from '@/features/workbench/useWork'

const STATUSES = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
] as const

const POINTS = [1, 2, 3, 5, 8] as const

/** The comment thread: what happened, findings along the way, and the resolution.
 *
 * Separate from the form on purpose. Comments post immediately with their own button and
 * never ride the Save — the API keeps them off the item's row version, so two people can
 * comment on the same item at once and neither gets "modified by someone else".
 */
function CommentThread({ itemId }: { itemId: string }) {
  const { data } = useWorkItemComments(itemId)
  const add = useAddWorkItemComment(itemId)
  const [draft, setDraft] = useState('')

  const comments = data?.comments ?? []

  const post = () => {
    const body = draft.trim()
    if (!body) return
    add.mutate(body, { onSuccess: () => setDraft('') })
  }

  return (
    <Field label="Comments & resolution" hint="Stamped and permanent — a correction is a new comment.">
      <div
        className="space-y-2 rounded border border-border p-2"
        style={{ backgroundColor: 'color-mix(in srgb, var(--fg-muted) 4%, transparent)' }}
      >
        {comments.length === 0 ? (
          <p className="px-1 text-xs text-fg-muted">
            Nothing yet. When this closes, say here what actually happened.
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="rounded bg-surface px-2 py-1.5">
              <p className="text-xs text-fg-muted">
                <span className="font-medium text-fg">{comment.authorName || 'Unknown'}</span>
                {' · '}
                {new Date(comment.createdAt).toLocaleString()}
              </p>
              <p className="whitespace-pre-wrap text-sm text-fg">{comment.body}</p>
            </div>
          ))
        )}

        {add.error ? <ErrorState error={add.error} /> : null}

        <div className="flex items-end gap-2">
          <textarea
            className={inputClass}
            rows={2}
            placeholder="Add a comment or resolution…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button onClick={post} disabled={!draft.trim() || add.isPending}>
            {add.isPending ? 'Posting…' : 'Post'}
          </Button>
        </div>
      </div>
    </Field>
  )
}

export function EditWorkItemDialog({ item, onClose }: { item: WorkItem; onClose: () => void }) {
  const update = useUpdateWorkItem()
  const { data: projectData } = useProjects({})
  const { data: session } = useSession()
  const { data: sprintData } = useSprints()

  const [form, setForm] = useState({
    title: item.title,
    description: item.description,
    status: item.status,
    priority: item.priority,
    points: item.points === null ? '' : String(item.points),
    project_id: item.projectId ?? '',
    milestone_id: item.milestoneId ?? '',
    sprint_id: item.sprintId ?? '',
    reference_url: item.referenceUrl ?? '',
  })

  // Held apart from `form` because it is a pair — an id and the matching name — and the
  // generic `set` helper below only knows how to carry a single string out of an event.
  const [assignee, setAssignee] = useState<AssigneeValue>({
    userId: item.assigneeUserId ?? '',
    name: item.assigneeName,
  })

  const isTracker = item.itemType === 'tracker'
  // Editable after creation because the first live tracker somebody else made read 0 of 31
  // for a week: the sheet said "Complete", the default said "Completed", and the only fix
  // was delete-and-recreate, which loses the sync history.
  const [source, setSource] = useState<TrackerSource>({
    source_path: item.tracker?.sourcePath ?? '',
    source_sheet: item.tracker?.sourceSheet ?? '',
    source_status_column: item.tracker?.sourceStatusColumn ?? '',
    source_done_value: item.tracker?.sourceDoneValue ?? '',
  })

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = () => {
    update.mutate(
      {
        id: item.id,
        title: form.title,
        description: form.description,
        status: form.status,
        priority: form.priority,
        points: form.points ? Number(form.points) : null,
        project_id: form.project_id || null,
        milestone_id: form.milestone_id || null,
        // A tracker never sits in a sprint; the API refuses it too.
        sprint_id: isTracker ? null : form.sprint_id || null,
        ...assigneeFields(assignee),
        reference_url: form.reference_url || null,
        ...(isTracker
          ? {
              source_path: source.source_path || null,
              source_sheet: source.source_sheet || null,
              source_status_column: source.source_status_column || null,
              source_done_value: source.source_done_value || null,
            }
          : {}),
        // Sent so a concurrent edit conflicts instead of silently overwriting.
        row_version: item.rowVersion,
      },
      { onSuccess: onClose },
    )
  }

  // Completed sprints are hidden as move targets — moving work into a finished
  // fortnight would change history. The item's current sprint stays listed even if
  // completed, so opening the dialog never silently relocates the item.
  const sprintOptions = (sprintData?.sprints ?? []).filter(
    (sprint) => sprint.status !== 'completed' || sprint.id === item.sprintId,
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        // Wide enough for two columns: what the work is on the left, where it sits on
        // the right. At 512px everything stacked and the description got three lines.
        className="max-h-[92vh] w-full max-w-5xl overflow-auto rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${item.title}`}
      >
        <h2 className="mb-4 font-heading text-2xl text-fg">Edit work item</h2>

        {update.error ? (
          <div className="mb-4">
            <ErrorState error={update.error} />
          </div>
        ) : null}

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-3">
              <Field label="Title">
                <input className={inputClass} value={form.title} onChange={set('title')} autoFocus />
              </Field>

              <Field label="Description">
                <textarea
                  className={`${inputClass} resize-y`}
                  rows={10}
                  value={form.description}
                  onChange={set('description')}
                />
              </Field>

              <CommentThread itemId={item.id} />
            </div>

            <div className="min-w-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status">
                  <select className={inputClass} value={form.status} onChange={set('status')}>
                    {STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Priority">
                  <select className={inputClass} value={form.priority} onChange={set('priority')}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </Field>
              </div>

              <Field label="Project" hint="Links this work to a project's rollup.">
                <SearchSelect
                  value={form.project_id}
                  onChange={(value) =>
                    // A milestone belongs to one project, so changing project drops it.
                    setForm((current) => ({
                      ...current,
                      project_id: value,
                      milestone_id: value === current.project_id ? current.milestone_id : '',
                    }))
                  }
                  options={projectOptions(projectData?.projects ?? [], session?.userId)}
                  emptyLabel="None"
                />
              </Field>

              <MilestoneField
                projectId={form.project_id}
                value={form.milestone_id}
                onChange={(value) => set('milestone_id')({ target: { value } })}
              />

              <div className="grid grid-cols-2 gap-3">
                <Field label="Points">
                  <select className={inputClass} value={form.points} onChange={set('points')}>
                    <option value="">Unpointed</option>
                    {POINTS.map((point) => (
                      <option key={point} value={point}>
                        {point}
                      </option>
                    ))}
                  </select>
                </Field>
                {isTracker ? null : (
                  <Field label="Sprint">
                    <select className={inputClass} value={form.sprint_id} onChange={set('sprint_id')}>
                      <option value="">Backlog</option>
                      {sprintOptions.map((sprint) => (
                        <option key={sprint.id} value={sprint.id}>
                          {sprint.name}
                          {sprint.status === 'active' ? ' — active' : ''}
                          {sprint.status === 'completed' ? ' — completed' : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>

              <AssigneePicker value={assignee} onChange={setAssignee} />

              {isTracker ? (
                <TrackerSourceFields
                  value={source}
                  onChange={(key, next) => setSource((current) => ({ ...current, [key]: next }))}
                />
              ) : null}

              <Field label="Reference URL" hint="A change record, runbook or ticket.">
                <input
                  className={inputClass}
                  value={form.reference_url}
                  onChange={set('reference_url')}
                />
              </Field>
            </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.title.trim() || update.isPending}
            onClick={submit}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}
